import React, { ReactElement } from "react";
import { useQuery } from "urql";
import { FileSpreadsheet } from "lucide-react";

import { TimeSeries, DateHelper } from "../../../common/src";
import { TimeSeriesLines } from "../../../components/src/charts/TimeSeriesLines";
import { seriesFromTimeSeries } from "../../../components/src/charts/chartTheme";

import { useQueryState } from "../../../components/src/use-query-state";
import { exportToXlsx, sheetType } from "../../../components/src/common/exportToXlsx";
import { getFundPerformance } from "../../../graphql/src/performance/queries";

import type { FundPerformance, FundPortfolioPerformance } from "../types.generated";

export const Aum2Page = (): ReactElement => {
    const todaysDate = DateHelper.dateToString(DateHelper.today()).substring(0, 10);
    const yesterday = DateHelper.dateToString(DateHelper.dateAddDays(new Date(todaysDate), -1));

    const [endDate] = useQueryState("endDate", yesterday);

    const [{ data, fetching, error }] = useQuery<{ fundPerformance?: FundPortfolioPerformance }>({
        query: getFundPerformance,
        variables: { endDate: endDate, includeDetails: false }
    });

    if (fetching) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    const tss: TimeSeries[] = [];
    const funds: FundPerformance[] = [];
    for (const fund of data?.fundPerformance?.fundPerformances ?? []) {
        if (fund) funds.push(fund);
    }

    for (const fund of funds) {
        const dates = fund.dates ?? [];
        const values = fund.values ?? [];
        const cashFlows = fund.cashFlows ?? [];
        let start = 0;
        while (start < dates.length && Math.abs(values[start] ?? 0) < 1e-6 && Math.abs(cashFlows[start] ?? 0) < 1e-6) {
            start += 1;
        }
        if (start >= dates.length) continue;

        let name = fund.fund?.name;
        if (!name) name = fund.fundId;
        tss.push(
            new TimeSeries(
                dates.slice(start),
                values.slice(start).map((value) => value / 1000000.0),
                name
            )
        );
    }

    const portfolio = data?.fundPerformance;
    if (portfolio?.dates?.length) {
        const totalAumTimeSeries = new TimeSeries(
            portfolio.dates,
            portfolio.values.map((value) => value / 1000000.0),
            "Total AUM"
        );
        const totalCashFlowTimeSeries = new TimeSeries(
            portfolio.dates,
            portfolio.cashFlows.map((cashFlow) => (Math.abs(cashFlow) > 1e-4 ? cashFlow / 1000000.0 : 0)),
            "Flows"
        );
        tss.push(totalAumTimeSeries);
        tss.push(totalCashFlowTimeSeries.cumSum());
    }

    const series = tss.map((ts) => seriesFromTimeSeries(ts));

    return (
        <div className="full-height">
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <button
                        id="export-excel"
                        className="export-link"
                        type="button"
                        onClick={() => {
                            if (!funds.length) return;
                            const table: sheetType[] = [];
                            const sheetNames: string[] = [];
                            const usedSheetNames: Record<string, boolean> = {};
                            for (const fund of funds) {
                                const dates = fund.dates ?? [];
                                if (!dates.length) continue;
                                let sheetName = fund.fund?.name;
                                if (!sheetName) sheetName = fund.fundId;
                                if (usedSheetNames[sheetName]) sheetName = sheetName + " " + fund.fundId;
                                usedSheetNames[sheetName] = true;
                                sheetNames.push(sheetName);
                                const rows = [];
                                for (let index = 0; index < dates.length; index++) {
                                    rows.push({
                                        dates: dates[index],
                                        values: fund.values[index],
                                        cashFlows: fund.cashFlows[index]
                                    });
                                }
                                table.push(rows);
                            }
                            if (table.length) exportToXlsx(table, "aum.xlsx", sheetNames);
                        }}
                    >
                        <FileSpreadsheet className="Excel" />
                    </button>
                </div>
            </div>

            <TimeSeriesLines
                series={series}
                showLegend
                legendLayout="wrap"
                zoom
                ariaLabel="Assets under management"
                height={640}
                style={{ width: "100%", height: "80vh" }}
            />
        </div>
    );
};
